import React, { useCallback, useEffect, useState } from "react";
import { settings } from "app";
import {
  ArrayField,
  BooleanField,
  Datagrid,
  FunctionField,
  ReferenceField,
  SimpleShowLayout,
  TextField,
  Title,
} from "react-admin";
import { useLocation, useParams } from "react-router";
import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import BackButtonToolbar from "features/BackButtonToolbar";
import dataProvider from "server/dataProvider";
import { administrationRequests } from "api/administrationApi";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import UpdateClassGradeDialog from "./UpdateClassGradeDialog";
import AddIndividualExamDialog from "./AddIndividualExamDialog";
import EditIndividualExamDialog from "./EditIndividualExamDialog";
import MidSemesterGrades from "./MidSemesterGrades";
import SemesterGrades from "./SemesterGrades";
import { studentRequests } from "api/studentApi";
import { PracticalExamStatusChoices, PracticalExamStatusColorMap } from "utils/choices";
import WorkedOutDialog from "components/LearningActivity/MakeUps/WorkedOutDialog";

const PartialRecord = () => {
  const { id, disciplineId } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paramsEducationId = params.get("educationId");

  const studentName = location?.state?.studentName;
  const educationId = location?.state?.educationId || paramsEducationId;
  const disciplineName = location?.state?.disciplineName;

  const [disciplineStudentRecord, setDisciplineStudentRecord] = useState(null);

  const fetchDisciplineData = useCallback(async () => {
    if (!id && !disciplineId) return;
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentRecordDisciplineById(id, disciplineId),
        null,
        "GET",
      );
      setDisciplineStudentRecord(data);
    } catch (err) {
      console.error(err);
    }
  }, [id, disciplineId]);

  useEffect(() => {
    fetchDisciplineData();
  }, [fetchDisciplineData]);

  const attendancesBySemester = disciplineStudentRecord?.classAttendances?.reduce((acc, curr) => {
    if (!acc[curr.semester]) {
      acc[curr.semester] = [];
    }
    acc[curr.semester].push(curr);
    return acc;
  }, {});

  const semesters = attendancesBySemester ? Object.keys(attendancesBySemester).sort() : [];

  return (
    <>
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <Title title={`Студентски картон ${studentName || ""}`} />
        <BackButtonToolbar />

        <Typography
          color="primary"
          sx={{ ml: 2, fontSize: 18 }}
        >
          Дисциплина:{" "}
          {disciplineName ? (
            disciplineName
          ) : (
            <ReferenceField
              record={{ disciplineId }}
              source="disciplineId"
              reference={administrationRequests.getDisciplinesSelect()}
            >
              <TextField
                source="name"
                sx={{ fontSize: 18 }}
              />
            </ReferenceField>
          )}
        </Typography>

        {disciplineStudentRecord && (
          <SimpleShowLayout record={disciplineStudentRecord}>
            <Card
              key={disciplineStudentRecord}
              sx={{ mb: 2, pt: 1, pl: 2 }}
            >
              <CardContent>
                <Grid
                  spacing={2}
                  container
                >
                  {disciplineStudentRecord?.practicalExam && (
                    <Grid
                      item
                      xs={12}
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        color="primary"
                        sx={{ mb: 1 }}
                        textAlign="left"
                      >
                        Практически изпит
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={
                            PracticalExamStatusChoices.find(c => c.id === disciplineStudentRecord.practicalExam.status)
                              ?.name
                          }
                          color={PracticalExamStatusColorMap[disciplineStudentRecord.practicalExam.status] ?? "default"}
                          size="small"
                        />
                        {disciplineStudentRecord.practicalExam.gradeText && (
                          <Typography>{disciplineStudentRecord.practicalExam.gradeText}</Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    sx={{ mb: 5 }}
                  >
                    <Typography
                      color="primary"
                      sx={{ mb: 1 }}
                      textAlign="left"
                    >
                      Присъствия по семестри
                    </Typography>
                    {semesters.length > 0 ? (
                      semesters?.map((semNum, index) => (
                        <Box key={`semester-${semNum}-${index}`}>
                          <Typography
                            color={"primary.dark"}
                            fontWeight={"bold"}
                          >
                            Семестър {semNum}
                          </Typography>
                          <ArrayField
                            record={{ currentSemesterData: attendancesBySemester[semNum] }}
                            source="currentSemesterData"
                            label=""
                          >
                            <Datagrid
                              bulkActionButtons={false}
                              rowClick={false}
                              resource="classAttendances"
                              sx={[
                                settings.style.detailsDatagrid,
                                { "& .RaDatagrid-headerCell": { textAlign: "left" } },
                              ]}
                            >
                              <ReferenceField
                                source="classTypeId"
                                reference={administrationRequests.getRoomClassTypesSelect()}
                                label="Тип занятие"
                                sortable={false}
                              >
                                <TextField source="name" />
                              </ReferenceField>
                              <TextField
                                source="date"
                                label="Дата"
                              />
                              <FunctionField
                                label="Час"
                                render={rec => `${rec.startTime.slice(0, 5)} - ${rec.endTime.slice(0, 5)}`}
                              />
                              <BooleanField
                                source="attended"
                                label="Присъствие"
                              />
                              <TextField
                                source="topicName"
                                label="Тема"
                              />
                              <FunctionField
                                label="Бележка (оценка)"
                                render={rec => (rec.gradeNote ? rec.gradeNote : "")}
                              />
                              <FunctionField
                                label="Оценка"
                                render={rec => (
                                  <SecuredComponent permission={permissions.canUpdateClassGrade}>
                                    <Box
                                      sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}
                                    >
                                      {rec?.gradeText && <Typography>{rec?.gradeFullText}</Typography>}
                                      <UpdateClassGradeDialog
                                        grade={rec?.grade}
                                        classId={rec?.classId}
                                        educationId={educationId}
                                        label={rec?.gradeText ? "Промени оценка" : "Добави оценка"}
                                        fetchDisciplineData={fetchDisciplineData}
                                      />
                                    </Box>
                                  </SecuredComponent>
                                )}
                              />
                              <FunctionField
                                render={rec =>
                                  rec.isForMakeUp && (
                                    <SecuredComponent permission={permissions.canManageMakeUps}>
                                      <WorkedOutDialog
                                        studentEducationId={id}
                                        classId={rec.classId}
                                        onSuccess={fetchDisciplineData}
                                      />
                                    </SecuredComponent>
                                  )
                                }
                              />
                            </Datagrid>
                          </ArrayField>
                        </Box>
                      ))
                    ) : (
                      <Datagrid
                        data={[]}
                        sx={settings.style.detailsDatagrid}
                      />
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ mb: 5 }}
                  >
                    <Typography
                      color="primary"
                      sx={{ mb: 1 }}
                    >
                      Учебни групи
                    </Typography>
                    <ArrayField
                      source="studyGroups"
                      label=""
                    >
                      <Datagrid
                        bulkActionButtons={false}
                        rowClick={false}
                        resource="studyGroups"
                        sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
                      >
                        <FunctionField
                          label="Семестър"
                          render={rec => rec.semester}
                        />
                        <TextField
                          source="academicYearName"
                          label="Учебна година"
                        />
                        <FunctionField
                          label="Учебна група"
                          render={rec => (rec.studyGroup ? rec.studyGroup : "-")}
                        />
                      </Datagrid>
                    </ArrayField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ mb: 5 }}
                  >
                    <Typography
                      color="primary"
                      sx={{ mb: 1 }}
                    >
                      Преподаватели
                    </Typography>
                    <ArrayField
                      source="professors"
                      label=""
                    >
                      <Datagrid
                        bulkActionButtons={false}
                        rowClick={false}
                        resource="professors"
                        sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
                      >
                        <ReferenceField
                          source="classTypeId"
                          reference={administrationRequests.getRoomClassTypesSelect()}
                          label="Тип занятие"
                          sortable={false}
                        >
                          <TextField source="name" />
                        </ReferenceField>

                        <FunctionField
                          label="Преподавател"
                          render={rec =>
                            rec.secondName
                              ? `${rec.scientificDegreeName} ${rec.firstName} ${rec.secondName} ${rec.lastName}`
                              : `${rec.scientificDegreeName} ${rec.firstName} ${rec.lastName}`
                          }
                        />
                      </Datagrid>
                    </ArrayField>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    xl={6}
                    sx={{ mb: 5 }}
                  >
                    <Typography
                      color="primary"
                      sx={{ mb: 1 }}
                    >
                      Текущи оценки
                    </Typography>
                    <ArrayField
                      source="individualExams"
                      label=""
                    >
                      <Datagrid
                        bulkActionButtons={false}
                        rowClick={false}
                        resource="individualExams"
                        sx={[
                          settings.style.detailsDatagrid,
                          { "& .RaDatagrid-headerCell": { textAlign: "left" } },
                          { mb: 1 },
                        ]}
                      >
                        <FunctionField
                          label="Семестър"
                          render={rec => rec.semester}
                        />
                        <TextField
                          source="examName"
                          label="Изпит"
                        />
                        <ReferenceField
                          source="type"
                          label="Тип"
                          reference={studentRequests.getIndividualExamsTypesSelect()}
                        >
                          <TextField source="name" />
                        </ReferenceField>
                        <TextField
                          label="Оценка"
                          source="gradeFullText"
                        />
                        <SecuredComponent permission={permissions.canManageIndividualExams}>
                          <FunctionField
                            render={rec => (
                              <EditIndividualExamDialog
                                individualExamId={rec.id}
                                fetchDisciplineData={fetchDisciplineData}
                              />
                            )}
                          />
                        </SecuredComponent>
                      </Datagrid>
                      <SecuredComponent permission={permissions.canManageIndividualExams}>
                        <AddIndividualExamDialog
                          disciplineId={disciplineId}
                          educationId={educationId}
                          fetchDisciplineData={fetchDisciplineData}
                        />
                      </SecuredComponent>
                    </ArrayField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    xl={6}
                    sx={{ mb: 5 }}
                  >
                    <Typography
                      color="primary"
                      sx={{ mb: 1 }}
                    >
                      Текущи оценки (среден успех)
                    </Typography>
                    <ArrayField
                      source="individualExamAverageGrades"
                      label=""
                    >
                      <Datagrid
                        bulkActionButtons={false}
                        rowClick={false}
                        resource="individualExamAverageGrades"
                        sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
                      >
                        <FunctionField
                          label="Семестър"
                          render={rec => rec.semester}
                        />
                        <TextField
                          source="examTypeText"
                          label="Тип"
                        />
                        <TextField
                          source="averageGradeFullText"
                          label="Средна оценка"
                        />
                      </Datagrid>
                    </ArrayField>
                  </Grid>
                  <SecuredComponent permission={permissions.canPreviewStudentEducationInfo}>
                    <Grid
                      item
                      xs={12}
                      xl={6}
                      sx={{ mb: 5 }}
                    >
                      <SemesterGrades
                        studentEducationId={id}
                        disciplineId={disciplineId}
                      />
                    </Grid>
                  </SecuredComponent>
                  <SecuredComponent permission={permissions.canPreviewMidSemesterGrades}>
                    <Grid
                      item
                      xs={12}
                      xl={6}
                      sx={{ mb: 5 }}
                    >
                      <MidSemesterGrades
                        studentEducationId={id}
                        disciplineId={disciplineId}
                      />
                    </Grid>
                  </SecuredComponent>
                </Grid>
              </CardContent>
            </Card>
          </SimpleShowLayout>
        )}
      </Box>
    </>
  );
};

export default PartialRecord;
